import React, { useContext } from 'react';
import { Grid } from '@mui/material';
import { TranslationKey } from './../../../../../../../types/translation/TranslationKey';
import {
  ProductTransaction,
  ProductTransactionParentType,
  ProductTransactionStatus,
} from '../../../../../../../types/productTransaction';
import ErrorBox from '../../../../../Common/ErrorBox';
import { StockLocationRoleAssignmentContext } from '../../../../../../../context/StockLocationRoleAssignmentContext';
import { CompanyContext } from '../../../../../../../context/CompanyContext';
import { StockLocationRole } from '../../../../../../../types/stock_location_role_assignment';
import { CompanyRoleAssignmentContext } from '../../../../../../../context/CompanyRoleAssignmentContext';
import WarningBox from '../../../../../Common/WarningBox';
import { t } from '../../../../../../../types/translation/Translator';
import { ProductMasterData } from '../../../../../../../types/product_master_data/productMasterData';
import { Container } from '../../../../../../../types/container';
import { StockLocationContext } from '../../../../../../../context/StockLocationContext';
import { OrderContext } from '../../../../../../../context/OrderContext';
import { UserContext } from '../../../../../../../context/UserContext';
import { TextInput } from '../../../../../../components/TextInput';
import { DateTimeUtil } from '../../../../../../../util/date.util';

interface ProcessOrderTransactionHeaderProps {
  transaction: ProductTransaction;
  stockLocationId: string;

  productMasterData?: ProductMasterData;
  container?: Container;

  showParentInfo: boolean;

  scanRequired: boolean;
  disabled: boolean;
}

export default function ProcessOrderTransactionHeader({
  transaction,
  stockLocationId,
  productMasterData,
  container,
  showParentInfo,
  scanRequired,
  disabled,
}: ProcessOrderTransactionHeaderProps) {
  const { currentCompany } = useContext(CompanyContext);
  const { stockLocations } = useContext(StockLocationContext);
  const { orders } = useContext(OrderContext);

  const { hasCompanyRole } = useContext(CompanyRoleAssignmentContext);
  const { hasStockLocationRole } = useContext(StockLocationRoleAssignmentContext);
  const { companyUsers, locale } = useContext(UserContext);

  const isViewer = !hasStockLocationRole(currentCompany.id, stockLocationId, StockLocationRole.STOCK_LOCATION_USER);

  function ReasonForFailure() {
    if (transaction.reasonForFailure) {
      return (
        <Grid item xs={12}>
          <ErrorBox error={`Reason for failure: ${transaction.reasonForFailure}`} />
        </Grid>
      );
    }

    return null;
  }

  function ScanObligation() {
    if (!disabled && scanRequired && !isViewer && transaction.status === ProductTransactionStatus.created) {
      return (
        <Grid item xs={12}>
          <WarningBox warning={t(TranslationKey.scanRequiredWarning).singular.label} />
        </Grid>
      );
    }

    return null;
  }

  function ProductMasterData() {
    if (!productMasterData) {
      return (
        <Grid item xs={6}>
          <TextInput
            disabled
            value={container?.identifier}
            label={t(TranslationKey.container).singular.label}
            onChange={() => {}}
          />
        </Grid>
      );
    }

    return (
      <>
        <Grid item xs={6}>
          <TextInput
            disabled
            value={productMasterData.productName}
            label={t(TranslationKey.productName).singular.label}
            onChange={() => {}}
          />
        </Grid>
        <Grid item xs={6}>
          <TextInput
            disabled
            value={productMasterData.productNumber}
            label={t(TranslationKey.productNumber).singular.label}
            onChange={() => {}}
          />
        </Grid>
      </>
    );
  }

  function SourceStockLocation() {
    if (!transaction.product.fromStockLocationId) return null;
    return (
      <Grid item xs={6}>
        <TextInput
          label={t(TranslationKey.sourceStockLocation).singular.label}
          disabled
          value={stockLocations.get(transaction.product.fromStockLocationId)?.name || ''}
          onChange={() => {}}
        />
      </Grid>
    );
  }

  function DestinationStockLocation() {
    if (!transaction.product.toStockLocationId) return null;

    return (
      <Grid item xs={6}>
        <TextInput
          label={t(TranslationKey.destinationStockLocation).singular.label}
          disabled
          value={stockLocations.get(transaction.product.toStockLocationId)?.name || ''}
          onChange={() => {}}
        />
      </Grid>
    );
  }

  function ParentInformation() {
    if (showParentInfo && transaction.parentId && transaction.parentType === ProductTransactionParentType.order) {
      return (
        <Grid item xs={6}>
          <TextInput
            label={t(TranslationKey.order).singular.label}
            disabled
            value={orders.get(transaction.parentId || '')?.number || ''}
            onChange={() => {}}
          />
        </Grid>
      );
    }
    return null;
  }

  function PickedInformation() {
    if (!transaction.pickedBy || !transaction.pickedAt) return null;

    return (
      <Grid item xs={12}>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <TextInput
              disabled
              label={t(TranslationKey.pickedBy).singular.label}
              value={companyUsers.get(transaction.pickedBy)?.email}
              onChange={() => {}}
            />
          </Grid>
          <Grid item xs={6}>
            <TextInput
              disabled
              label={t(TranslationKey.pickedAt).singular.label}
              value={DateTimeUtil.toLocaleDateTimeString(locale, new Date(transaction.pickedAt))}
              onChange={() => {}}
            />
          </Grid>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid item xs={12}>
      <Grid container spacing={1}>
        <ReasonForFailure />
        <ScanObligation />
        <ProductMasterData />
        <SourceStockLocation />
        <DestinationStockLocation />
        <ParentInformation />
        <PickedInformation />
      </Grid>
    </Grid>
  );
}
